import { useCallback, useEffect, useRef } from 'react';

import * as S from './styled/OTPInput.styled';

/**
 * OTPInput component for handling OTP (One-Time Password) input fields.
 *
 * @param {Object} props - The component props.
 * @param {Array} props.value - The current value of the OTP input fields.
 * @param {Function} props.setValue - Function to update the value of the OTP input fields.
 * @param {boolean} props.loading - Flag indicating if the input fields should be disabled.
 * @param {Function} [props.onFilledOnce] - Callback function to be called once all input fields are filled for the first time.
 * @param {number} [props.length = 4] - The number of OTP input fields.
 *
 * @returns {JSX.Element} The rendered OTPInput component.
 */
const OTPInput = ({ value, setValue, loading, onFilledOnce, length = 4 }) => {
	const isFilledOnce = useRef(false);
	const containerRef = useRef(null);

	const handlePaste = useCallback(
		(e) => {
			e.preventDefault();

			let data = (e.clipboardData || window.clipboardData).getData('text');
			if (data) {
				data = data.replace(/\D/g, '').substring(0, length);
				if (data.length === length) {
					setValue(data.split(''));
				}
			}
		},
		[setValue]
	);

	useEffect(() => {
		if (loading) return;

		document.addEventListener('paste', handlePaste);

		return () => {
			document.removeEventListener('paste', handlePaste);
		};
	}, [handlePaste, loading]);

	useEffect(() => {
		if (loading) return;

		const emptyIndex = value.findIndex((v) => v === null);
		if (emptyIndex !== -1) {
			// focus on the first empty input
			const input = containerRef.current;
			const inputs = input?.querySelectorAll('input');
			inputs?.[emptyIndex].focus();
		} else {
			if (!isFilledOnce.current) {
				isFilledOnce.current = true;
				onFilledOnce && onFilledOnce(value);
			}
		}
	}, [value, loading]);

	const handleValueChange = (val, i) => {
		const newValue = [...value];
		newValue[i] = val;
		setValue(newValue);
	};

	const handleKeyUp = useCallback((e, i) => {
		if (e.key === 'Backspace') {
			containerRef.current?.querySelectorAll('input')[i - 1]?.focus();
		}
	}, []);

	const handleFocus = useCallback((e) => {
		if (e.target.value) {
			e.target.select();
		}
	}, []);

	return (
		<S.Container className="otp-input" ref={containerRef}>
			{[...Array(length)].map((_, i) => (
				<S.StyledStringInput
					key={i}
					value={value[i]}
					setValue={(val) => {
						handleValueChange(val, i);
					}}
					maxlength={1}
					onKeyUp={(e) => {
						handleKeyUp(e, i);
					}}
					onFocus={handleFocus}
					disabled={loading}
					placeholder="0"
				/>
			))}
		</S.Container>
	);
};

export default OTPInput;
